import React, {useCallback, useState} from 'react';

import {WBSlateElementType} from '../../../components/Slate/WBSlate/types';
import {isEmptySlateValue} from '../../../components/Slate/WBSlate/utils';
import {useEntityPageContext, useEntityPageUpdaterContext} from '../context';
import {EmptyState} from '../EmptyState';
import {getEntityProfileInitialValue} from '../util';
import {TeamReadmeInner} from './TeamReadmeInner';
import {TeamReadmeModal} from './TeamReadmeModal';
import {TeamReadmePreview} from './TeamReadmePreview';

type TeamReadmeSectionProps = {
  editing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
};

export const TeamReadmeSection: React.FC<TeamReadmeSectionProps> = ({
  editing,
  onEdit,
  onCancelEdit,
}) => {
  const {entityName, entityIsTeam, teamReadme, readOnly} =
    useEntityPageContext();
  const {setTeamReadme} = useEntityPageUpdaterContext();

  const [localValue, setLocalValue] = useState(
    teamReadme ?? getEntityProfileInitialValue(entityName, entityIsTeam)
  );
  const [numSaves, setNumSaves] = useState(0);

  const onChange = (newValue: WBSlateElementType[]) => {
    setLocalValue(newValue);
  };

  const save = useCallback(() => {
    setNumSaves(saves => saves + 1);
    if (isEmptySlateValue(localValue)) {
      setTeamReadme(null);
      setLocalValue(getEntityProfileInitialValue(entityName, entityIsTeam));
    } else {
      setTeamReadme(localValue);
    }
    onCancelEdit();
  }, [localValue, entityName, entityIsTeam, setTeamReadme, onCancelEdit]);

  const renderContent = (
    contentValue: WBSlateElementType[],
    contentReadOnly: boolean
  ) => (
    <TeamReadmeInner
      readOnly={contentReadOnly}
      value={contentValue}
      onChange={onChange}
    />
  );

  return (
    <>
      {teamReadme != null ? (
        <TeamReadmePreview key={numSaves}>
          {renderContent(teamReadme, true)}
        </TeamReadmePreview>
      ) : (
        <EmptyState
          iconName="document"
          header="Create your team's central resource"
          body="Share context, instructions, and resources to help team members succeed."
          buttonText="Create"
          onClickButton={onEdit}
        />
      )}
      {editing && (
        <TeamReadmeModal onSave={save} onClose={onCancelEdit}>
          {renderContent(localValue, readOnly)}
        </TeamReadmeModal>
      )}
    </>
  );
};
